import React from 'react';

export interface CardProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'className'> {
  hover?: boolean;
  padding?: 'sm' | 'md' | 'lg';
  mb?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 8 | 10 | 12;
  mt?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 8 | 10 | 12;
  fullWidth?: boolean;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';
  fixed?: boolean;
  inset?: boolean;
  zIndex?: number;
  bg?: 'default' | 'dark';
  shadow?: boolean;
  animate?: 'slideUp' | 'fadeIn' | 'none';
  children: React.ReactNode;
}

const paddingMap: Record<'sm' | 'md' | 'lg', string> = {
  sm: 'p-4',
  md: 'p-6',
  lg: 'p-8',
};

const mbMap: Record<number, string> = {
  0: 'mb-0',
  1: 'mb-1',
  2: 'mb-2',
  3: 'mb-3',
  4: 'mb-4',
  5: 'mb-5',
  6: 'mb-6',
  8: 'mb-8',
  10: 'mb-10',
  12: 'mb-12',
};

const mtMap: Record<number, string> = {
  0: 'mt-0',
  1: 'mt-1',
  2: 'mt-2',
  3: 'mt-3',
  4: 'mt-4',
  5: 'mt-5',
  6: 'mt-6',
  8: 'mt-8',
  10: 'mt-10',
  12: 'mt-12',
};

const maxWidthMap: Record<string, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  full: 'max-w-full',
};

const animateMap: Record<string, string> = {
  slideUp: 'animate-[slideUp_0.3s_cubic-bezier(0.4,0,0.2,1)]',
  fadeIn: 'animate-[fadeIn_0.2s_ease]',
  none: '',
};

export const Card: React.FC<CardProps> = ({
  hover = true,
  padding = 'md',
  mb,
  mt,
  fullWidth,
  maxWidth,
  fixed,
  inset,
  zIndex,
  bg = 'default',
  shadow,
  animate,
  children,
  ...props
}) => {
  const baseStyles =
    bg === 'default'
      ? 'bg-white/5 rounded-2xl border border-white/10'
      : 'bg-[#141414]/95 rounded-[20px] border border-white/10';
  
  const hoverStyles = hover
    ? 'hover:bg-white/10 hover:border-[#3b82f6]/20 hover:-translate-y-0.5 hover:shadow-2xl hover:shadow-black/30'
    : '';

  const paddingClass = paddingMap[padding];
  const classes = [
    baseStyles,
    paddingClass,
    hoverStyles,
    mb !== undefined && mbMap[mb],
    mt !== undefined && mtMap[mt],
    fullWidth && 'w-full',
    maxWidth && maxWidthMap[maxWidth],
    fixed && 'fixed',
    inset && 'inset-0',
    zIndex !== undefined && `z-[${zIndex}]`,
    shadow && 'shadow-2xl shadow-black/50',
    animate && animateMap[animate],
  ].filter(Boolean).join(' ');

  return (
    <div
      className={classes}
      {...props}
    >
      {children}
    </div>
  );
};

